import { onMounted, onUnmounted } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { broadcastService } from 'src/services/app/broadcast.service'
import { log } from 'src/services/app/log.service'
import { themeService } from 'src/services/app/theme.service'
import { BROADCAST_TOPICS } from 'src/services/consts/app.const'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import {
  BROADCAST_UPDATE_TYPES,
  FCM_ACTIONS,
  FCM_PENDING_PREFIXES,
} from 'src/services/consts/fcm.const'
import { createPendingId } from 'src/services/helpers/fcm.helper'
import { useAuthStore } from 'src/stores/auth'
import { useFcmCache } from 'src/composables/fcm/shared/useFcmCache'
import { useFcmPendingUpdates } from 'src/composables/fcm/shared/useFcmPendingUpdates'
import { markNotificationPreferencesStale } from 'src/composables/notification-preferences/query/cache-handlers'

/**
 * Sets up all app broadcast subscriptions (logout sync, FCM data sync).
 * Call once in the app root (e.g. AppIndex). Cleans up on unmount.
 */
export function useBroadcastChannel() {
  const authStore = useAuthStore()
  const fcmCache = useFcmCache()
  const fcmPending = useFcmPendingUpdates()
  const queryClient = useQueryClient()

  const unsubscribes = []

  onMounted(() => {
    subscribeTo(BROADCAST_TOPICS.LOGOUT, () => {
      log.info('[Broadcast] Received logout from other tab')
      authStore.logout({ fromOtherTab: true })
    })

    subscribeTo(BROADCAST_TOPICS.FCM_DATA, (payload) => {
      if (!payload?.updateType) return
      log.info('[Broadcast] Received update from other tab:', payload.updateType, { payload })
      switch (payload.updateType) {
        case BROADCAST_UPDATE_TYPES.TICKET:
          fcmCache.setTicketInCache(payload.ticketData)
          if (payload.ticketId) {
            fcmPending.remove(createPendingId(FCM_PENDING_PREFIXES.TICKET, payload.ticketId))
          }
          break
        case BROADCAST_UPDATE_TYPES.DELETE_TICKET:
          fcmCache.removeTicketFromCache(payload.ticketId)
          fcmCache.closeOpenTicketDrawer(payload.ticketId)
          if (payload.ticketId) {
            fcmPending.remove(createPendingId(FCM_PENDING_PREFIXES.DELETE_TICKET, payload.ticketId))
          }
          break
        case BROADCAST_UPDATE_TYPES.APPROVAL:
          fcmCache.setApprovalInCache(payload.approvalData)
          if (payload.approvalId) {
            fcmPending.remove(createPendingId(FCM_PENDING_PREFIXES.APPROVAL, payload.approvalId))
          }
          if (payload.approvalData?.ticketId) {
            fcmPending.remove(
              createPendingId(FCM_PENDING_PREFIXES.TICKET, payload.approvalData.ticketId)
            )
          }
          break
        case BROADCAST_UPDATE_TYPES.DELETE_AUTH:
          fcmCache.removeApprovalFromCache(payload.approvalId)
          fcmCache.closeOpenApprovalModal(payload.approvalId)
          if (payload.approvalId) {
            fcmPending.remove(createPendingId(FCM_PENDING_PREFIXES.DELETE_AUTH, payload.approvalId))
          }
          break
        case BROADCAST_UPDATE_TYPES.MESSAGE:
          if (payload.action === FCM_ACTIONS.NEW) {
            fcmCache.addMessageToCache({
              ticketId: payload.ticketId,
              message: payload.message,
            })
          } else if (payload.action === FCM_ACTIONS.DELETE) {
            fcmCache.removeMessageFromCache({
              ticketId: payload.ticketId,
              messageId: payload.messageId,
            })
          }
          break
        case BROADCAST_UPDATE_TYPES.ACCOUNT_DATA:
          fcmCache.setAccountDataInCache(payload.accountData)
          if (payload.accountData?.settings?.theme) {
            themeService.applyTheme(payload.accountData.settings.theme)
          }
          fcmCache.applyPendingDetailQueries()
          fcmPending.clearAll()
          break
        case BROADCAST_UPDATE_TYPES.COMPANIES_DIRECTORY:
          fcmCache.setCompaniesDirectoryInCache(payload.updatedCompanies)
          break
        case BROADCAST_UPDATE_TYPES.QUERY_DATA: {
          const { queryKey, data } = payload
          const previous = queryClient.getQueryData(queryKey)
          queryClient.setQueryData(queryKey, data)
          // The Slack query broadcasts on every fetch; mark preferences stale only on a status change
          if (
            queryKey[0] === QUERY_KEYS.SLACK_USER_INFO &&
            (previous?.enabled !== data.enabled ||
              previous?.accountSlackEnabled !== data.accountSlackEnabled)
          ) {
            markNotificationPreferencesStale(queryClient)
          }
          break
        }
        case BROADCAST_UPDATE_TYPES.SETTINGS_UPDATE:
          queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], (oldData) => {
            if (!oldData) return oldData
            return {
              ...oldData,
              settings: { ...oldData.settings, ...payload.settings },
            }
          })
          themeService.applyTheme(payload.settings.theme)
          break
      }
    })

    log.info('[Broadcast] Subscriptions registered')
  })

  onUnmounted(() => {
    unsubscribes.forEach((fn) => fn())
    broadcastService.closeChannel()
  })

  function subscribeTo(event, callback) {
    const unsubscribe = broadcastService.subscribe(event, callback)
    unsubscribes.push(unsubscribe)
    return unsubscribe
  }
}

export function broadcastTicket(ticketId, ticketData) {
  log.info('[Broadcast] Sending ticket update:', ticketId)
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.TICKET,
    ticketId,
    ticketData,
  })
}

export function broadcastTicketRemove(ticketId) {
  log.info('[Broadcast] Sending ticket remove:', ticketId)
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.DELETE_TICKET,
    ticketId,
  })
}

export function broadcastApprovalRemove(approvalId) {
  log.info('[Broadcast] Sending approval remove:', approvalId)
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.DELETE_AUTH,
    approvalId,
  })
}

export function broadcastApproval(approvalId, approvalData) {
  log.info('[Broadcast] Sending approval update:', approvalId)
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.APPROVAL,
    approvalId,
    approvalData,
  })
}

export function broadcastNewMessage(ticketId, message) {
  log.info('[Broadcast] Sending new message:', ticketId)
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.MESSAGE,
    action: FCM_ACTIONS.NEW,
    ticketId,
    message,
  })
}

export function broadcastDeleteMessage(ticketId, messageId) {
  log.info('[Broadcast] Sending message delete:', ticketId, messageId)
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.MESSAGE,
    action: FCM_ACTIONS.DELETE,
    ticketId,
    messageId,
  })
}

export function broadcastAccountData(accountData) {
  log.info('[Broadcast] Sending account data')
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.ACCOUNT_DATA,
    accountData,
  })
}

export function broadcastCompaniesDirectory(updatedCompanies) {
  log.info('[Broadcast] Sending companies directory update:', updatedCompanies)
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.COMPANIES_DIRECTORY,
    updatedCompanies,
  })
}

/** Replaces one whole query key in other tabs. Patches, removals and side effects keep their own type. */
export function broadcastQueryData(queryKey, data) {
  log.info('[Broadcast] Sending query data:', queryKey)
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.QUERY_DATA,
    queryKey,
    data,
  })
}

export function broadcastSettings(settings) {
  log.info('[Broadcast] Sending settings update')
  broadcastService.post(BROADCAST_TOPICS.FCM_DATA, {
    updateType: BROADCAST_UPDATE_TYPES.SETTINGS_UPDATE,
    settings,
  })
}
